import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import {
  isAdditionalFeRole,
  isPrimaryFeRole,
  normalizeFeDispatchRole,
  resolveAdditionalFeUserIds,
} from '@/components/forms/parentTicket/feDispatchRole'
import { parentLevelFeSchedules, scheduleSpansMultipleDays } from '@/components/forms/parentTicket/feDispatchUtils'
import type { ParentTicketWithMeta } from '@/store/parentTicket'

function normalizeScheduleTime(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback
  return value.length >= 5 ? value.slice(0, 5) : value
}

function buildSelectionForSchedule(
  meta: ParentTicketWithMeta,
  schedule: NonNullable<ParentTicketWithMeta['feSchedules']>[number],
  feName: string,
  role: 'primary' | 'additional',
  storeId: number | null,
  additionalSlot?: number,
): FEDispatchSelection {
  const store = meta.store
  const scheduleDate = schedule.schedule_date?.slice(0, 10) ?? ''
  const endDate = (schedule.schedule_end_date ?? schedule.schedule_date)?.slice(0, 10) ?? scheduleDate
  const spans = scheduleSpansMultipleDays(scheduleDate, endDate)
  const hasStore = storeId != null && Boolean(store)
  const incidentLocation = meta.incidentLocation
  const locationAddress = schedule.location_address ?? incidentLocation?.address ?? incidentLocation?.name ?? ''

  return {
    locationType: hasStore ? 'store' : 'location',
    storeId,
    storeCode: hasStore ? store?.storeCode ?? store?.branchCode ?? '' : '',
    storeName: hasStore ? store?.storeName ?? '' : locationAddress,
    storeAddress: hasStore ? store?.branchAddress ?? store?.locationAddress ?? '' : locationAddress,
    latitude: hasStore ? store?.latitude ?? store?.location?.latitude ?? null : schedule.latitude ?? incidentLocation?.latitude ?? null,
    longitude: hasStore ? store?.longitude ?? store?.location?.longitude ?? null : schedule.longitude ?? incidentLocation?.longitude ?? null,
    locationDisplayName: hasStore ? undefined : locationAddress,
    incidentLocationMunicipality: hasStore ? null : incidentLocation?.municipality ?? null,
    incidentLocationProvince: hasStore ? null : incidentLocation?.province ?? null,
    incidentLocationCountry: hasStore ? null : incidentLocation?.country ?? null,
    feUserId: schedule.fe_user_id,
    feName,
    scheduleDate,
    scheduleTime: normalizeScheduleTime(schedule.schedule_time, '09:00'),
    scheduleEndTime: normalizeScheduleTime(schedule.schedule_end_time, '10:00'),
    contactNumberOverride: schedule.contact_number_override ?? undefined,
    spansMultipleDays: spans,
    scheduleEndDate: spans ? endDate : undefined,
    role,
    additionalSlot: role === 'additional' ? additionalSlot : undefined,
    childTicketId: schedule.child_ticket_id ?? null,
    sourceScheduleId: schedule.id,
    child_ticket_id: schedule.child_ticket_id ?? null,
  }
}

type TicketWithAdditionalMeta = ParentTicketWithMeta & {
  additionalEngineers?: Array<{ user_id?: number; full_name?: string | null } | null>
  backupEngineers?: Array<{ user_id?: number; full_name?: string | null } | null>
  secondBackupEngineer?: { full_name?: string | null } | null
}

/** Build primary/additional dispatch selections from ticket fe_schedules (edit mode hydration + post-persist sync).
 * Kept separate from buildLiveFEDispatchSelection(): this path reconstructs from persisted schedules and
 * engineer meta rather than a live reference location + dispatch window.
 */
export function buildDispatchSelectionsFromTicket(meta: ParentTicketWithMeta): {
  primary: FEDispatchSelection | null
  additionalFes: FEDispatchSelection[]
} {
  if (!meta.ticketSubmittedAt && meta.dispatchPlan) {
    const assignments = meta.dispatchPlan.assignments ?? []
    return {
      primary: assignments.find((a) => a.role === 'primary') ?? null,
      additionalFes: assignments.filter((a) => a.role === 'additional'),
    }
  }
  const store = meta.store
  const schedules = parentLevelFeSchedules(meta.feSchedules ?? [])
  const primarySchedule = schedules.find((s) => isPrimaryFeRole(s.role)) ?? schedules[0] ?? null

  if (!primarySchedule) {
    return { primary: null, additionalFes: [] }
  }

  const primaryFeName =
    meta.primaryEngineer?.full_name ??
    primarySchedule.field_engineer?.name ??
    'Assigned engineer'

  const primary = buildSelectionForSchedule(
    meta,
    primarySchedule,
    primaryFeName,
    'primary',
    store?.storeId ?? null,
  )

  const additionalSchedules = schedules.filter(
    (s) => isAdditionalFeRole(s.role) && s.fe_user_id !== primary.feUserId,
  )

  const metaWithAdditional = meta as TicketWithAdditionalMeta
  const additionalEngineers = Array.isArray(metaWithAdditional.additionalEngineers)
    ? metaWithAdditional.additionalEngineers
    : Array.isArray(metaWithAdditional.backupEngineers)
      ? metaWithAdditional.backupEngineers
      : []
  const additionalFeUserIds = resolveAdditionalFeUserIds(metaWithAdditional)

  const orderedAdditionalIds = Array.from(new Set(
    additionalFeUserIds
      .map(Number)
      .filter((id) => id > 0 && id !== primary.feUserId),
  ))
  const schedulesByEngineerId = new Map(
    additionalSchedules.map((schedule) => [schedule.fe_user_id, schedule]),
  )
  const orderedAdditionalSchedules = orderedAdditionalIds.length > 0
    ? [
        ...orderedAdditionalIds.map(
          (feUserId) =>
            schedulesByEngineerId.get(feUserId) ?? {
              ...primarySchedule,
              fe_user_id: feUserId,
              role: 'additional' as const,
              field_engineer: undefined,
              child_ticket_id: null,
            },
        ),
        ...additionalSchedules.filter(
          (schedule) => !orderedAdditionalIds.includes(schedule.fe_user_id),
        ),
      ]
    : additionalSchedules

  const additionalFes: FEDispatchSelection[] = orderedAdditionalSchedules.map((schedule, index) => {
    const engineerMeta =
      additionalEngineers.find((fe) => fe?.user_id === schedule.fe_user_id) ??
      (index === 0
        ? meta.additionalEngineer ?? meta.backupEngineer
        : index === 1
          ? metaWithAdditional.secondBackupEngineer
          : null)
    const feName =
      engineerMeta?.full_name ??
      schedule.field_engineer?.name ??
      `Additional FE #${index + 1}`
    return buildSelectionForSchedule(
      meta,
      {
        ...schedule,
        role: normalizeFeDispatchRole(schedule.role),
      },
      feName,
      'additional',
      store?.storeId ?? null,
      index + 1,
    )
  })

  return { primary, additionalFes }
}
